import { ButtonLevel, ButtonSize, FileTrigger, Text, TextSize } from '@vizit/atoms';
import { UploadCloud02Icon } from '@vizit/icons';
import { cva, cx } from '@vizit/theme';
import { formatFileSize, formatFileTypes } from '@vizit/utils';
import React from 'react';
import type { DropEvent } from 'react-aria';
import { type DropItem, DropZone, type DropZoneProps, type FileDropItem } from 'react-aria-components';
import { DropZoneError } from './dropzone-error';
import { useDropZone } from './dropzone-provider';
interface _DropZoneProps extends Omit<DropZoneProps, 'children'> {
  /**
   * The children of the DropZone.
   *
   * @optional
   * @type React.ReactNode
   */
  children?: React.ReactNode;
  /**
   * The function to call when an error occurs.
   *
   * @optional
   * @type (error: string) => void
   */
  onDropError?: (error: string) => void;
}

const classes = cva({
  base: 'flex flex-col items-center border-solid px-12 py-6',
  variants: {
    hasError: {
      true: 'border-error-primary border-[2px]',
      false: 'border-secondary border',
    },
  },
});

function _DropZone(props: _DropZoneProps) {
  const { children, className, onDrop, onDropError, ...other } = props;

  const {
    acceptedFileTypes = ['image/png', 'image/jpeg', 'image/webp'],
    allowsMultiple,
    maxFileSize = 6 * 1024 * 1024, // 6MB
    droppedFiles,
    fileErrors,
    addFiles,
    addError,
    resetErrors,
  } = useDropZone();

  const hasFiles = React.useMemo(() => droppedFiles.length > 0, [droppedFiles]);
  const alwaysShowDropZone = React.useMemo(() => hasFiles && allowsMultiple, [allowsMultiple, hasFiles]);

  const acceptedFileTypesText = formatFileTypes(acceptedFileTypes);
  const maxFileSizeText = formatFileSize(maxFileSize);

  const handleGetResolvedFiles = React.useCallback(async (items: DropItem[]): Promise<File[]> => {
    try {
      return await Promise.all(
        items.filter((item: DropItem): item is FileDropItem => item.kind === 'file').map((item) => item.getFile()),
      );
    } catch {
      throw new Error('An error occurred while processing dropped files.');
    }
  }, []);

  const handleDrop = React.useCallback(
    (event: DropEvent) => {
      // Reset the file errors
      resetErrors();

      const { items } = event;

      handleGetResolvedFiles(items)
        .then((resolvedFiles) => {
          onDrop?.(event);

          addFiles(resolvedFiles);
        })
        .catch((error) => {
          addError('An error occurred while processing dropped files.');

          onDropError?.(error);
        });
    },
    [onDrop, addFiles, addError, handleGetResolvedFiles, onDropError, resetErrors],
  );

  const handleSelect = React.useCallback(
    (fileList: FileList | null) => {
      // Reset the file errors
      resetErrors();

      const files = [...(fileList as unknown as File[])];

      addFiles(files);
    },
    [addFiles, resetErrors],
  );

  return (
    <>
      {(!hasFiles || alwaysShowDropZone) && (
        <div aria-label="Upload files" className={cx('flex flex-col gap-6 min-h-[180px] min-w-[286px]', className)}>
          <DropZone className={classes({ hasError: fileErrors.size > 0 })} onDrop={handleDrop} {...other}>
            <div className="flex flex-col items-center justify-center gap-2">
              <div className="flex items-center justify-center border border-primary text-secondary p-5 mb-4">
                <UploadCloud02Icon />
              </div>
              <div className="flex w-full items-center justify-center">
                <FileTrigger
                  acceptedFileTypes={acceptedFileTypes}
                  allowsMultiple={allowsMultiple}
                  className="font-semibold p-0 pr-2 h-auto"
                  level={ButtonLevel['Text-Primary']}
                  size={ButtonSize.Md}
                  onSelect={handleSelect}
                >
                  Click to upload
                </FileTrigger>
                <Text size={TextSize.Sm}>or drag and drop</Text>
              </div>
              <Text size={TextSize.Xs}>{`${acceptedFileTypesText} (max. ${maxFileSizeText})`}</Text>
              {children}
            </div>
          </DropZone>
          <DropZoneError />
        </div>
      )}
    </>
  );
}

export { _DropZone as DropZone };
export type { _DropZoneProps as DropZoneProps };
